'use client'

import { useState, useEffect } from 'react'
import type { StoredTemplate } from '@/lib/template-storage'

interface Props {
  title: string
  subtitle: string
  loadFn: () => StoredTemplate[]
  saveFn: (t: StoredTemplate[]) => void
  getActiveIdFn: () => string
  setActiveIdFn: (id: string) => void
  variables: { key: string; label: string }[]
  previewFn: (text: string) => string
}

export function TemplateSection({
  title, subtitle, loadFn, saveFn, getActiveIdFn, setActiveIdFn,
  variables, previewFn,
}: Props) {
  const [templates, setTemplates] = useState<StoredTemplate[]>([])
  const [activeId, setActiveId] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editText, setEditText] = useState('')
  const [showPreview, setShowPreview] = useState(false)
  const [newName, setNewName] = useState('')
  const [showNewForm, setShowNewForm] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    setTemplates(loadFn())
    setActiveId(getActiveIdFn())
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function persist(updated: StoredTemplate[]) {
    setTemplates(updated)
    saveFn(updated)
  }

  function handleSetActive(id: string) {
    setActiveId(id)
    setActiveIdFn(id)
  }

  function handleEdit(template: StoredTemplate) {
    setEditingId(template.id)
    setEditText(template.text)
    setShowPreview(false)
  }

  function handleSave() {
    if (!editingId) return
    persist(templates.map((t) => t.id === editingId ? { ...t, text: editText } : t))
    setEditingId(null)
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  function handleCancel() {
    setEditingId(null)
    setEditText('')
    setShowPreview(false)
  }

  function handleAdd() {
    if (!newName.trim()) return
    const id = `tpl_${Date.now()}`
    const newTemplate: StoredTemplate = {
      id,
      name: newName.trim(),
      text: templates[0]?.text || '',
      isDefault: false,
    }
    persist([...templates, newTemplate])
    setNewName('')
    setShowNewForm(false)
    handleEdit(newTemplate)
  }

  function handleDelete(id: string) {
    const updated = templates.filter((t) => t.id !== id)
    persist(updated)
    if (activeId === id) handleSetActive(updated[0]?.id || '')
    if (editingId === id) setEditingId(null)
  }

  return (
    <section className="mb-12">
      <div className="flex items-end justify-between mb-5 gap-4 flex-wrap">
        <div>
          <h2 className="text-[18px] font-semibold text-ink tracking-tight">{title}</h2>
          <div className="text-[13px] text-ink-muted mt-0.5">{subtitle}</div>
        </div>
        {!showNewForm && (
          <button onClick={() => setShowNewForm(true)} className="btn btn-sm btn-ghost">
            + Новый шаблон
          </button>
        )}
      </div>

      {showNewForm && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-5 surface-card rounded-sm p-3">
          <input
            type="text"
            placeholder="Название шаблона"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            className="input w-full flex-1"
            autoFocus
          />
          <button onClick={handleAdd} disabled={!newName.trim()} className="btn btn-sm btn-primary w-full sm:w-auto">
            Создать
          </button>
          <button
            onClick={() => { setShowNewForm(false); setNewName('') }}
            className="btn btn-sm btn-ghost w-full sm:w-auto"
          >
            Отмена
          </button>
        </div>
      )}

      {saved && (
        <div className="mb-5 px-3 py-2.5 rounded-sm bg-st-delivered/8 border border-st-delivered/30 text-st-delivered text-[12px] tracking-wide">
          Шаблон сохранён
        </div>
      )}

      <div className="space-y-2 mb-8">
        {templates.map((tpl) => {
          const isActive = activeId === tpl.id
          return (
            <div
              key={tpl.id}
              className={`surface-card rounded-sm p-4 sm:p-5 transition-all relative ${isActive ? 'border-gold' : ''}`}
            >
              {isActive && <span className="absolute left-0 top-0 bottom-0 w-[2px] bg-gold" />}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <button
                    onClick={() => handleSetActive(tpl.id)}
                    aria-label="Сделать активным"
                    className={`size-4 rounded-full border flex items-center justify-center transition-colors shrink-0 ${
                      isActive ? 'border-gold bg-gold' : 'border-rule-strong hover:border-ink-faint'
                    }`}
                  >
                    {isActive && <div className="size-1.5 rounded-full bg-paper" />}
                  </button>
                  <span className="text-[14px] text-ink font-medium truncate">{tpl.name}</span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {tpl.isDefault && (
                      <span className="px-2 py-0.5 rounded-sm bg-bone text-ink-muted text-[10px] tracking-[0.12em] uppercase">
                        Встроенный
                      </span>
                    )}
                    {isActive && (
                      <span className="px-2 py-0.5 rounded-sm bg-gold/12 text-gold-deep text-[10px] tracking-[0.12em] uppercase font-medium">
                        Активен
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap sm:shrink-0">
                  <button onClick={() => handleEdit(tpl)} className="btn btn-sm btn-ghost">
                    Редактировать
                  </button>
                  {!tpl.isDefault && (
                    <button
                      onClick={() => handleDelete(tpl.id)}
                      className="text-[11px] tracking-[0.06em] text-st-unpaid hover:underline px-2"
                    >
                      Удалить
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-3 text-[11.5px] text-ink-faint truncate font-mono leading-relaxed">
                {tpl.text.slice(0, 140)}…
              </div>
            </div>
          )
        })}
      </div>

      {editingId && (
        <div className="surface-card rounded-md p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
            <div>
              <div className="text-[12px] text-ink-muted">Редактирование</div>
              <h3 className="text-[16px] font-semibold text-ink mt-0.5 truncate max-w-[70vw] sm:max-w-none">
                {templates.find((t) => t.id === editingId)?.name}
              </h3>
            </div>
            <button
              onClick={() => setShowPreview(!showPreview)}
              className={`btn btn-sm ${showPreview ? 'btn-gold' : 'btn-ghost'}`}
            >
              {showPreview ? 'Скрыть превью' : 'Превью'}
            </button>
          </div>

          <p className="mb-2 text-[12px] text-ink-muted">
            Нажмите на поле — его метка вида {'${…}'} добавится в конец текста, а при отправке заменится значением из заказа.
          </p>
          <div className="flex flex-wrap gap-1.5 mb-4 items-center">
            <span className="text-[11px] tracking-[0.08em] uppercase text-ink-muted font-semibold mr-2">
              Поля заказа
            </span>
            {variables.map((v) => (
              <button
                type="button"
                key={v.key}
                title={v.key}
                onClick={() => setEditText((prev) => prev + v.key)}
                className="max-w-full min-h-7 px-2.5 py-1.5 rounded-sm text-left break-words text-[11px] bg-bone text-ink-2 hover:bg-rule-strong hover:text-ink transition-colors"
              >
                {v.label}
              </button>
            ))}
          </div>

          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            rows={16}
            className="w-full px-4 py-3 text-[13px] font-mono leading-relaxed bg-paper border border-rule-strong rounded-sm focus:outline-none focus:border-gold transition-colors resize-y"
          />

          {showPreview && (
            <div className="mt-5">
              <div className="text-[11px] tracking-[0.08em] uppercase text-ink-muted font-semibold mb-2">
                Превью с примером
              </div>
              <div className="bg-bone/40 border border-rule rounded-md p-5 text-[14px] leading-[1.6] text-ink-2 whitespace-pre-wrap">
                {previewFn(editText)}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 mt-5 flex-wrap">
            <button onClick={handleSave} className="btn btn-primary">Сохранить</button>
            <button onClick={handleCancel} className="btn btn-ghost">Отмена</button>
          </div>
        </div>
      )}
    </section>
  )
}
